/**
 * 専用パネルを持つ会議タブの本体。並べ方の異なる 3 案を切り替えられる。
 *
 * 素材は 3 つで、3 案の違いは「最初に何が目に入るか」だけ:
 *   案1 議題リスト … 数字と宿題を混ぜて緊急度順に上位N件
 *   案2 3ブロック  … 数字 / 宿題 / 改善 を区切って積む
 *   案3 1画面完結  … 左に数字・右にやること。資料は畳む
 *
 * 数字はファネル集計（/api/funnel）と成熟度そろえの集計（/api/meeting-board/cohort）、
 * 宿題・決定・課題は前回会議（/api/meeting-board/previous）から取る。
 * ラベルや業務上の意味づけはすべて配信側が持ち、ここは並べ方だけを担当する。
 */
import { useMemo, useState } from 'react'
import {
  AlertTriangle, ChevronDown, ChevronRight,
  FlaskConical, MessageSquareText,
} from 'lucide-react'

import { Badge } from '@/components/Badge'
import { useBoardCohort, useBoardSection } from '@/hooks/useMeetingBoard'
import { useFunnelOverview } from '@/hooks/useFunnel'
import { BoardReportViewer } from '@/components/BoardReportViewer'
import { cn } from '@/lib/utils'

export type CohortPanelVariant = '1' | '2' | '3'

/** 会議で見る件数（本番では deploy 設定から） */
const CONFIG = { agendaTop: 6, carryover: 6 }

// ── データ取得 ─────────────────────────────────────────────
interface StageCounts { juchu?: number; application?: number; entry?: number; construction?: number; activated?: number; cancelled?: number; churned?: number; paid?: number }
interface FunnelMonth { period: string; keiei: { stageCounts: StageCounts } }
interface FunnelOverview { months: FunnelMonth[]; computedAt: string | null }
interface Candidate {
  id: string; kind: 'action' | 'decision' | 'issue' | 'agenda'
  summary: string; owner: string | null; dueAt: string | null
  confidence: number; reviewStatus: string; createdAt: string; runId: string
  /** 会議がまだ取り込めていないときに出す「入る予定の見え方」。実際の会議内容ではない */
  placeholder?: boolean
}

function usePanelData(tab: string) {
  const funnel = useFunnelOverview(6) as { data?: FunnelOverview; isLoading: boolean }
  const candidates = useBoardSection<{
    items: Candidate[]
    placeholder?: { note: string; items: Candidate[] } | null
  }>('previous', tab)
  const real = candidates.data?.items ?? []
  const placeholder = candidates.data?.placeholder ?? null
  return {
    funnel: funnel.data,
    // 会議がまだ無いときは「入る予定の見え方」を出す（サンプルと明示する）
    candidates: real.length > 0 ? real : (placeholder?.items ?? []),
    isPlaceholder: real.length === 0 && (placeholder?.items.length ?? 0) > 0,
    placeholderNote: placeholder?.note ?? null,
    isLoading: funnel.isLoading || candidates.isLoading,
  }
}

// ── 成熟度そろえの指標（/api/meeting-board/cohort） ─────────────────────

interface MonthCx { month: string; n: number; cx: number; rate: number | null; through: string; partial: boolean }
interface ConsultBucket { et: number; started: number; left: number; rate: number | null }
interface ConsultSplitSide {
  from: string; to: string; et: number; started: number; startRate: number | null
  yuu: number; noc: number; yuuRate: number | null; nocRate: number | null
}
interface LandingRow { month: string; n: number; activated: number; cancelled: number; weak: number; strong: number; base: number }
interface SegmentOutlier { axis: string; segment: string; n: number; rate: number; overall: number; ratio: number; z: number }
interface CxSurge { axis: string; segment: string; n: number; rate: number; prevRate: number; deltaPt: number }

interface CohortMetrics {
  asOf: string
  product: string | null
  /** 詳細資料の種別。deploy が配るディレクトリ名で、UI は意味を持たない。 */
  reportType?: string
  /**
   * 取組として合成するときの見出し・目的・方法。組織固有の言い回しなので配信側が持つ。
   * 未指定なら見出しだけ既定値にし、目的・方法は「未設定」として空欄で出す（勝手に埋めない）。
   */
  workstreamLabels?: {
    title?: string
    purpose?: string
    method?: string
    sourceObjectType?: string
  }
  /** 詳細資料ブロックの見出しと説明。未指定なら既定文言。 */
  reportTitle?: string
  reportDescription?: string
  fixed: {
    applications: { month: string; count: number; prevMonthCount: number }
    cancellations: { months: MonthCx[] }
    consultationProgress: {
      doneThrough: string | null
      partialFrom: string | null
      partialTo: string | null
      untouchedFrom: string | null
      done: ConsultBucket | null
      partial: ConsultBucket | null
      untouched: ConsultBucket | null
      pending: number
    }
    consultationSplit: {
      recent: ConsultSplitSide | null
      previous: ConsultSplitSide | null
      allStarted: number
      allYuuRate: number | null
      allNocRate: number | null
    }
    landing: LandingRow[]
    pendingConsulted: {
      total: number
      byMonth: Array<{ month: string; count: number; wait: number; passnw: number; yuu: number; noc: number }>
      stateLabels: Record<string, string>
    }
  }
  auto: {
    /** 旧版（成熟度がそろっていない）。互換のため残っているが画面では使わない */
    cxSurge: CxSurge[]
    outliers: SegmentOutlier[]
    /** 申込から N 日でそろえた見方（2026-08-10〜） */
    matured?: MaturedAuto
  }
}

interface ReasonCount { label: string; n: number }
interface MaturedWeek {
  label: string
  /** 投入日の範囲（この週に D+N を迎えた群） */
  from: string
  to: string
  n: number
  cx: number
  rate: number | null
  prevRate: number | null
  deltaPt: number | null
  /** |z|≧2。件数が小さいとほぼ false = 誤差の範囲 */
  significant: boolean
  z: number
  reasonsMajor: ReasonCount[]
  reasonsMinor: ReasonCount[]
}
interface MoverItem {
  axis: string
  segment: string
  recent: { cx: number; n: number; rate: number | null }
  previous: { cx: number; n: number; rate: number | null }
  deltaPt: number
  z: number
  direction: 'improved' | 'worsened' | 'flat'
  significant: boolean
}
interface MaturedAuto {
  days: number
  /** キャンセル全体のうち D+N までに起きる割合（この見方の捕捉率） */
  coverage: number | null
  weeks: MaturedWeek[]
  movers: {
    window: { recentFrom: string; recentTo: string; previousFrom: string; previousTo: string; weeks: number }
    items: MoverItem[]
    compared: number
  }
  volumeMix: {
    volume: Array<{ label: string; from: string; to: string; n: number; partial: boolean }>
    mix: Array<{ axis: string; segment: string; previousPct: number; recentPct: number; deltaPt: number; previousN: number; recentN: number }>
    mixWindow: { recentFrom: string; recentTo: string; previousFrom: string; previousTo: string }
  }
}

function useCohortMetrics(): CohortMetrics | undefined {
  return useBoardCohort<CohortMetrics>().data ?? undefined
}

/** "2026-07-17" → "7/17"（会議で読み上げやすい短縮） */
const md = (value: string | null): string =>
  value ? `${Number(value.slice(5, 7))}/${Number(value.slice(8, 10))}` : '—'

const KIND_LABEL: Record<Candidate['kind'], { label: string; tone: 'warning' | 'success' | 'destructive' | 'muted' }> = {
  action: { label: '宿題', tone: 'warning' },
  decision: { label: '決定', tone: 'success' },
  issue: { label: '課題', tone: 'destructive' },
  agenda: { label: '議題', tone: 'muted' },
}

// ── 共通パーツ ─────────────────────────────────────────────

/** 指標 1 行（ラベル / 大きい数字 / 前回差 / 補足）。 */
function MetricRow({ label, value, unit, delta, detail, tone, children }: {
  label: string
  value?: string
  unit?: string
  delta?: { text: string; good: boolean }
  detail?: string
  tone?: 'good' | 'bad'
  children?: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-baseline sm:gap-4">
      <p className="w-full shrink-0 text-xs font-medium text-muted-foreground sm:w-32">{label}</p>
      <div className="min-w-0 flex-1">
        {value && (
          <p className="flex flex-wrap items-baseline gap-1.5">
            <span className={cn(
              'text-3xl font-bold leading-none tabular-nums',
              tone === 'bad' ? 'text-red-600' : tone === 'good' ? 'text-emerald-600' : 'text-foreground',
            )}>{value}</span>
            {unit && <span className="text-sm text-muted-foreground">{unit}</span>}
            {delta && (
              <span className={cn(
                'ml-1 rounded px-1.5 py-0.5 text-xs font-semibold tabular-nums',
                delta.good ? 'bg-emerald-600/10 text-emerald-700' : 'bg-red-600/10 text-red-600',
              )}>{delta.text}</span>
            )}
          </p>
        )}
        {children}
        {detail && <p className="mt-1 text-xs tabular-nums text-muted-foreground">{detail}</p>}
      </div>
    </div>
  )
}

/** 補助数値のチップ（内訳を 1 行に並べる）。 */
function Chip({ label, value, sub, tone }: {
  label: string
  value: string
  sub?: string
  tone?: 'good' | 'bad' | 'warn'
}) {
  return (
    <span className={cn(
      'inline-flex items-baseline gap-1 rounded-md px-2 py-1 text-sm',
      tone === 'good' ? 'bg-emerald-600/10 text-emerald-700'
        : tone === 'bad' ? 'bg-red-600/10 text-red-600'
          : tone === 'warn' ? 'bg-amber-500/15 text-amber-700'
            : 'bg-muted text-muted-foreground',
    )}>
      <span className="text-xs opacity-80">{label}</span>
      <span className="font-bold tabular-nums">{value}</span>
      {sub && <span className="text-xs opacity-70 tabular-nums">{sub}</span>}
    </span>
  )
}

function SectionLabel({ children, hint }: { children: React.ReactNode; hint?: string }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2 px-1">
      <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{children}</p>
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  )
}

/** 案1 の議題リストに混ぜる「数字の行」。緊急度順に並べる。 */
function metricAgendaRows(metrics: CohortMetrics | undefined): Array<{
  key: string; severity: number; label: string; value: string
  delta?: { text: string; good: boolean }; note: string
}> {
  if (!metrics) return []
  const f = metrics.fixed
  const rows: Array<{ key: string; severity: number; label: string; value: string; delta?: { text: string; good: boolean }; note: string }> = []

  const cp = f.consultationProgress
  if (cp.untouched && cp.untouched.left > 0) {
    rows.push({
      key: 'untouched', severity: 100, label: 'コンサル未着手',
      value: `${cp.untouched.left.toLocaleString()}件`,
      note: `${md(cp.untouchedFrom)} 以降の投入分（着手率${cp.untouched.rate ?? '—'}%）。消化できているのは ${md(cp.doneThrough)} まで`,
    })
  }
  // 2026-08-10: 成熟度をそろえた見方に差し替え（旧 cxSurge / outliers は使わない）。
  // 案2・案3・現在の状況と同じ基準にして、同じ画面で取り方が食い違わないようにする。
  const mat = metrics.auto.matured
  if (mat) {
    const week = mat.weeks[0]
    if (week && week.cx > 0) {
      const top = week.reasonsMinor.filter((r) => r.label !== '(未設定)').slice(0, 3)
      rows.push({
        key: 'matured-cx', severity: 92, label: `直近週のキャンセル（申込から${mat.days}日時点）`,
        value: `${week.cx}件`,
        delta: week.deltaPt !== null && week.significant
          ? { text: `${week.deltaPt > 0 ? '+' : ''}${week.deltaPt}pt`, good: week.deltaPt <= 0 }
          : undefined,
        note: `申込 ${md(week.from)}〜${md(week.to)}・${week.n}件中 ${week.rate}%`
          + (top.length > 0 ? `／${top.map((r) => `${r.label}${r.n}`).join(' ')}` : '')
          + (week.significant ? '' : '（前週差は誤差の範囲）'),
      })
    }
    // 誤差の範囲を超えた層だけ。良い動きも出す
    for (const [i, x] of mat.movers.items.filter((x) => x.significant).slice(0, 2).entries()) {
      const improved = x.direction === 'improved'
      rows.push({
        key: `mover-${i}`, severity: improved ? 60 - i : 90 - i,
        label: `${improved ? 'キャンセル改善' : 'キャンセル悪化'}（${x.axis}）`,
        value: `${x.segment} ${x.recent.rate}%`,
        delta: { text: `${x.deltaPt > 0 ? '+' : ''}${x.deltaPt}pt`, good: improved },
        note: `申込から${mat.days}日時点。前は ${x.previous.rate}%（直近 ${x.recent.cx}/${x.recent.n}件）`,
      })
    }
  }

  const cxNow = f.cancellations.months[0]
  const cxPrev = f.cancellations.months[1]
  if (cxNow) {
    const diff = cxNow.rate !== null && cxPrev?.rate != null ? cxNow.rate - cxPrev.rate : null
    rows.push({
      key: 'cx', severity: diff !== null && diff > 0 ? 85 : 40, label: '月内CX率',
      value: `${cxNow.rate ?? '—'}%`,
      delta: diff !== null ? { text: `${diff > 0 ? '+' : ''}${diff.toFixed(1)}pt`, good: diff <= 0 } : undefined,
      note: `${cxNow.month.slice(5)}月投入 ${cxNow.cx}件/${cxNow.n.toLocaleString()}${cxNow.partial ? '（進行中）' : ''}。前月 ${cxPrev?.rate ?? '—'}%`,
    })
  }

  const recent = f.consultationSplit.recent
  const previous = f.consultationSplit.previous
  if (recent?.nocRate != null && previous?.nocRate != null) {
    const diff = recent.nocRate - previous.nocRate
    rows.push({
      key: 'noc', severity: diff < 0 ? 80 : 35, label: 'ノーコン率',
      value: `${recent.nocRate}%`,
      delta: { text: `${diff > 0 ? '+' : ''}${diff.toFixed(1)}pt`, good: diff > 0 },
      note: `直近2週 ${md(recent.from)}〜${md(recent.to)}。ノーコンのほうが通過もCXも良い`,
    })
  }

  const land = f.landing.find((l) => l.n >= 100) ?? f.landing[0]
  if (land) {
    rows.push({
      key: 'landing', severity: 30, label: '着地開通率',
      value: `${land.weak}〜${land.strong}%`,
      note: `${land.month.slice(5)}月投入（${land.n.toLocaleString()}件）。弱気〜強気`,
    })
  }
  return rows.sort((a, b) => b.severity - a.severity)
}

/** 固定枠: 毎週かならず見る数字 */
function FixedMetrics({ metrics }: { metrics: CohortMetrics | undefined }) {
  if (!metrics) return <Loading />
  const f = metrics.fixed
  const cp = f.consultationProgress
  const months = f.cancellations.months
  const cxNow = months[0]
  const cxPrev = months[1]
  const recent = f.consultationSplit.recent
  const previous = f.consultationSplit.previous
  const nocDiff = recent && previous && recent.nocRate !== null && previous.nocRate !== null
    ? recent.nocRate - previous.nocRate
    : null

  return (
    <div className="space-y-2">
      <SectionLabel hint={`${metrics.product ?? ''}・${metrics.asOf} 時点`}>固定（毎週見る）</SectionLabel>
      <div className="divide-y rounded-lg border bg-card shadow-sm">
        <MetricRow
          label={`当月の申込 ${f.applications.month.slice(5)}月`}
          value={f.applications.count.toLocaleString()}
          unit="件"
          detail={`前月 ${f.applications.prevMonthCount.toLocaleString()}件`}
        />
        <MetricRow
          label="月内CX率"
          value={cxNow ? `${cxNow.rate ?? '—'}%` : undefined}
          tone={cxNow && cxPrev && (cxNow.rate ?? 0) > (cxPrev.rate ?? 0) ? 'bad' : 'good'}
          delta={cxNow && cxPrev && cxNow.rate !== null && cxPrev.rate !== null
            ? { text: `${cxNow.rate - cxPrev.rate > 0 ? '+' : ''}${(cxNow.rate - cxPrev.rate).toFixed(1)}pt`, good: cxNow.rate <= cxPrev.rate }
            : undefined}
          detail={cxNow
            ? `${cxNow.month.slice(5)}月 ${cxNow.cx}件 / 投入${cxNow.n.toLocaleString()}${cxNow.partial ? `（${md(cxNow.through)}時点・進行中）` : ''}`
            : undefined}
        >
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {months.slice(1).map((m) => (
              <Chip key={m.month} label={`${m.month.slice(5)}月`} value={`${m.rate ?? '—'}%`} sub={`${m.cx}/${m.n.toLocaleString()}`} />
            ))}
          </div>
        </MetricRow>
        <MetricRow
          label="コンサル消化"
          value={md(cp.doneThrough)}
          unit="投入分まで"
          detail="着手率80%以上が続く先端（単日の凹みは未着手5件以下なら誤差扱い）"
        >
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {cp.done && <Chip label={`〜${md(cp.doneThrough)}`} value={`${cp.done.rate ?? '—'}%`} sub={`残${cp.done.left}`} tone="good" />}
            {cp.partial && <Chip label={`${md(cp.partialFrom)}〜${md(cp.partialTo ?? null)}`} value={`${cp.partial.rate ?? '—'}%`} sub={`残${cp.partial.left}`} tone="warn" />}
            {cp.untouched && <Chip label={`${md(cp.untouchedFrom)}〜`} value={`${cp.untouched.rate ?? '—'}%`} sub={`残${cp.untouched.left}`} tone="bad" />}
          </div>
        </MetricRow>
        <MetricRow
          label="ノーコン率"
          value={recent?.nocRate !== null && recent ? `${recent.nocRate}%` : undefined}
          tone={nocDiff !== null && nocDiff > 0 ? 'good' : undefined}
          delta={nocDiff !== null ? { text: `${nocDiff > 0 ? '+' : ''}${nocDiff.toFixed(1)}pt`, good: nocDiff > 0 } : undefined}
          detail={recent && previous
            ? `直近2週 ${md(recent.from)}〜${md(recent.to)}（着手${recent.startRate}%）／ その前2週 ${md(previous.from)}〜${md(previous.to)} は ${previous.nocRate}%（着手${previous.startRate}%）`
            : undefined}
        >
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {recent && <Chip label="有コン 直近" value={`${recent.yuuRate ?? '—'}%`} />}
            {previous && <Chip label="有コン 前2週" value={`${previous.yuuRate ?? '—'}%`} />}
          </div>
        </MetricRow>
        <MetricRow label="着地開通率" detail="弱気=保守的な前提 ／ 強気=資料の「7月の改善が続く場合」">
          <div className="space-y-1.5">
            {f.landing.map((l, i) => (
              <div key={l.month} className="flex flex-wrap items-baseline gap-2">
                <span className="w-12 shrink-0 text-xs tabular-nums text-muted-foreground">{l.month.slice(5)}月</span>
                <span className={cn('font-bold tabular-nums', i === 0 ? 'text-2xl' : 'text-lg text-muted-foreground')}>{l.weak}%</span>
                <span className="text-muted-foreground">〜</span>
                <span className={cn('font-bold tabular-nums', i === 0 ? 'text-2xl text-emerald-600' : 'text-lg text-muted-foreground')}>{l.strong}%</span>
                <span className="text-[11px] tabular-nums text-muted-foreground">
                  投入{l.n.toLocaleString()} / 開通{l.activated} / CX{l.cancelled}
                </span>
              </div>
            ))}
          </div>
        </MetricRow>
      </div>
    </div>
  )
}

/**
 * 自動枠: 成熟度をそろえた見方（2026-08-10 沖中指示で作り直し）
 *
 * 旧版は「投入から今日まで」で数えていたため、6月投入は2ヶ月ぶん、8月投入は数日ぶんの
 * キャンセルしか含まれず、担当者ごとに担当時期が偏ると実力以外の理由で率が動いていた。
 * 新版は **申込から N 日経過した時点** で全案件をそろえて数える。
 *
 * 件数が小さいので率の増減はたいてい誤差の範囲に入る。差を断定させないため、
 * 「誤差の範囲」を明示し、件数と理由の中身を主役にしている。
 */
function AutoMetrics({ metrics }: { metrics: CohortMetrics | undefined }) {
  if (!metrics) return null
  const m = metrics.auto.matured
  if (!m) return null
  const latest = m.weeks[0]
  const mv = m.movers
  const vm = m.volumeMix

  return (
    <div className="space-y-2">
      <SectionLabel hint={`申込から${m.days}日でそろえて比較`}>自動（今週の注目）</SectionLabel>

      <div className="divide-y rounded-lg border bg-card shadow-sm">
        {/* ① キャンセル: 週ごとの件数と率 */}
        <div className="px-4 py-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-xs font-medium text-muted-foreground">
              キャンセル（申込から{m.days}日時点）
            </p>
            {m.coverage !== null && (
              <p className="text-[11px] tabular-nums text-muted-foreground">
                この時点で起きるのはキャンセル全体の {m.coverage}%
              </p>
            )}
          </div>

          <div className="mt-2 flex flex-wrap gap-1.5">
            {m.weeks.map((w, i) => (
              <span
                key={w.from}
                className={cn(
                  'inline-flex flex-col rounded-md px-2.5 py-1.5',
                  i === 0 ? 'bg-muted' : 'bg-muted/50',
                )}
              >
                <span className="text-[11px] text-muted-foreground">
                  {w.label}（申込 {md(w.from)}〜{md(w.to)}）
                </span>
                <span className="flex items-baseline gap-1.5">
                  <span className={cn('font-bold tabular-nums', i === 0 ? 'text-2xl' : 'text-lg')}>
                    {w.cx}件
                  </span>
                  <span className="text-sm tabular-nums text-muted-foreground">/ {w.n}件中 {w.rate}%</span>
                </span>
              </span>
            ))}
          </div>

          {latest && latest.deltaPt !== null && (
            <p className="mt-2 text-xs">
              <span className="tabular-nums">
                前週比 {latest.deltaPt > 0 ? '+' : ''}{latest.deltaPt}pt
              </span>
              {latest.significant ? (
                <span className="ml-2 rounded bg-red-600/10 px-1.5 py-0.5 font-semibold text-red-600">
                  偶然では説明しにくい差
                </span>
              ) : (
                <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-muted-foreground">
                  誤差の範囲（件数が少なく、この差では判断できません）
                </span>
              )}
            </p>
          )}

          {/* 直近週の中身 */}
          {latest && latest.reasonsMajor.length > 0 && (
            <div className="mt-2.5 rounded-md border bg-muted/30 px-3 py-2">
              <p className="text-[11px] font-medium text-muted-foreground">
                直近週の {latest.cx}件 の内訳
              </p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {latest.reasonsMajor.map((r) => (
                  <Chip key={r.label} label={r.label} value={`${r.n}件`} />
                ))}
              </div>
              {latest.reasonsMinor.length > 0 && (
                <p className="mt-1.5 text-[11px] text-muted-foreground">
                  {latest.reasonsMinor.map((r) => `${r.label} ${r.n}`).join(' ／ ')}
                </p>
              )}
            </div>
          )}
        </div>

        {/* ② 層別の動き（良い方向も出す） */}
        <div className="px-4 py-3">
          <p className="text-xs font-medium text-muted-foreground">
            大きく動いた層（直近{mv.window.weeks}週 vs その前{mv.window.weeks}週・両方 n≧30）
          </p>
          {mv.items.length === 0 ? (
            <p className="mt-1 text-sm text-muted-foreground">比較できる層がありません</p>
          ) : (
            <ul className="mt-1.5 space-y-1">
              {mv.items.map((it) => {
                const worse = it.direction === 'worsened'
                return (
                  <li key={`${it.axis}-${it.segment}`} className="flex flex-wrap items-baseline gap-2">
                    <Badge tone="muted" className="rounded-md">{it.axis}</Badge>
                    <span className="font-medium">{it.segment}</span>
                    <span className={cn('text-lg font-bold tabular-nums', worse ? 'text-red-600' : 'text-emerald-600')}>
                      {it.recent.rate}%
                    </span>
                    <span className={cn(
                      'rounded px-1.5 py-0.5 text-xs font-semibold tabular-nums',
                      worse ? 'bg-red-600/10 text-red-600' : 'bg-emerald-600/10 text-emerald-700',
                    )}>
                      {it.deltaPt > 0 ? '+' : ''}{it.deltaPt}pt
                    </span>
                    <span className="text-[11px] tabular-nums text-muted-foreground">
                      前 {it.previous.rate}% ／ {it.recent.cx}/{it.recent.n}件
                    </span>
                    {!it.significant && (
                      <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">誤差の範囲</span>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
          <p className="mt-1.5 text-[11px] text-muted-foreground">
            {mv.compared}層を比較。キャンセルは下がるほうが良いので、緑＝改善・赤＝悪化。
          </p>
        </div>

        {/* ③ キャンセル以外で動いたもの（成熟度に関係なく確実に見えるもの） */}
        <div className="px-4 py-3">
          <p className="text-xs font-medium text-muted-foreground">キャンセル以外で動いたもの</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {vm.volume.map((v) => (
              <Chip
                key={v.from}
                label={`${v.label} 投入${v.partial ? '（進行中）' : ''}`}
                value={`${v.n.toLocaleString()}件`}
              />
            ))}
          </div>
          {vm.mix.length > 0 && (
            <ul className="mt-2 space-y-1">
              {vm.mix.map((x) => (
                <li key={`${x.axis}-${x.segment}`} className="flex flex-wrap items-baseline gap-2 text-sm">
                  <Badge tone="muted" className="rounded-md">{x.axis}</Badge>
                  <span className="font-medium">{x.segment}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {x.previousPct}% → {x.recentPct}%
                  </span>
                  <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-semibold tabular-nums">
                    {x.deltaPt > 0 ? '+' : ''}{x.deltaPt}pt
                  </span>
                  <span className="text-[11px] tabular-nums text-muted-foreground">
                    {x.previousN}→{x.recentN}件
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-1.5 text-[11px] text-muted-foreground">
            投入の量と中身の変化。ここは経過日数に左右されないので、そのまま読めます。
          </p>
        </div>
      </div>
    </div>
  )
}

function CandidateRow({ c, index }: { c: Candidate; index?: number }) {
  const k = KIND_LABEL[c.kind]
  return (
    <li className="flex flex-wrap items-start gap-3 px-4 py-3">
      {typeof index === 'number' && (
        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-bold tabular-nums">{index}</span>
      )}
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <Badge tone={k.tone} className="rounded-md">{k.label}</Badge>
          <span className={cn('font-medium', c.placeholder && 'text-muted-foreground')}>{c.summary}</span>
          {c.placeholder && <Badge tone="muted" className="rounded-md">サンプル</Badge>}
        </span>
        <span className="mt-0.5 block text-xs text-muted-foreground">
          {c.placeholder ? '会議が取り込まれると、ここに実際の内容が入ります' : <>
          {c.owner ? `担当 ${c.owner}` : '担当未設定'}
          {' ／ '}{c.dueAt ? `期限 ${new Date(c.dueAt).toLocaleDateString('ja-JP')}` : '期限未設定'}
          {' ／ '}確信度 {(c.confidence * 100).toFixed(0)}%
          {c.reviewStatus === 'pending' && ' ／ 未承認'}</>}
        </span>
      </span>
    </li>
  )
}


/** 詳細資料ブロック。どの資料を指すかは配信側の指定に従う。 */
function ReportBlock({ metrics }: { metrics: CohortMetrics | undefined }) {
  if (!metrics?.reportType) return null
  return (
    <BoardReportViewer
      reportType={metrics.reportType}
      title={metrics.reportTitle ?? '会議資料（詳細）'}
      description={metrics.reportDescription ?? '上に出していない章はこちらで確認できます。'}
      dateUnitLabel="の週"
      staleAfterDays={8}
    />
  )
}

// ── 案1: 議題リスト（数字と宿題を混ぜて上位N件） ──────────────
function Mock1({ tab }: { tab: string }) {
  const { candidates, isLoading } = usePanelData(tab)
  const metrics = useCohortMetrics()
  const metricRows = useMemo(() => metricAgendaRows(metrics), [metrics])

  const rows = useMemo(() => {
    const m = metricRows.map((x) => ({
      key: x.key, severity: x.severity,
      render: (i: number) => (
        <li key={x.key} className="flex flex-wrap items-start gap-3 px-4 py-3">
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-bold tabular-nums">{i}</span>
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-baseline gap-2">
              <Badge tone="muted" className="rounded-md">数字</Badge>
              <span className="text-xs text-muted-foreground">{x.label}</span>
              <span className="text-xl font-bold tabular-nums">{x.value}</span>
              {x.delta && (
                <span className={cn('rounded px-1.5 py-0.5 text-xs font-semibold tabular-nums',
                  x.delta.good ? 'bg-emerald-600/10 text-emerald-700' : 'bg-red-600/10 text-red-600')}>
                  {x.delta.text}
                </span>
              )}
            </span>
            <span className="mt-0.5 block text-xs text-muted-foreground">{x.note}</span>
          </span>
        </li>
      ),
    }))
    const order: Record<Candidate['kind'], number> = { issue: 75, action: 70, decision: 50, agenda: 30 }
    const c = [...candidates]
      .sort((a, b) => (order[b.kind] ?? 0) - (order[a.kind] ?? 0) || b.confidence - a.confidence)
      .map((x, i) => ({
        key: x.id, severity: (order[x.kind] ?? 0) - i,
        render: (idx: number) => <CandidateRow key={x.id} c={x} index={idx} />,
      }))
    return [...m, ...c].sort((a, b) => b.severity - a.severity)
  }, [metricRows, candidates])

  const shown = rows.slice(0, CONFIG.agendaTop)

  return (
    <div className="space-y-6">
      <MockNote title="案1: 今週の議題リスト" body="数字も宿題も同じリストに混ぜて、緊急度順に上位だけ出す。上から順に話せば会議が終わる形。" />
      <section className="space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="font-semibold">今週これだけは（上位{CONFIG.agendaTop}件）</h3>
          <span className="text-xs text-muted-foreground">{rows.length}件中 ／ {metrics?.product ?? ''}・{metrics?.asOf} 時点</span>
        </div>
        {isLoading || !metrics ? <Loading /> : (
          <ul className="divide-y rounded-lg border bg-card shadow-sm">{shown.map((r, i) => r.render(i + 1))}</ul>
        )}
      </section>
      <hr />
      <ReportBlock metrics={metrics} />
    </div>
  )
}

// ── 案2: 3ブロック（数字 / 宿題 / 改善） ────────────────────
function Mock2({ tab }: { tab: string }) {
  const { candidates, isLoading } = usePanelData(tab)
  const metrics = useCohortMetrics()
  const actions = candidates.filter((c) => c.kind === 'action' || c.kind === 'issue').slice(0, CONFIG.carryover)
  const decisions = candidates.filter((c) => c.kind === 'decision')

  return (
    <div className="space-y-6">
      <MockNote title="案2: 数字 → 宿題 → 改善 の3ブロック" body="種類ごとに区切って積む。どこに何があるか固定されるので、毎週同じ順で確認できる。" />
      {isLoading ? <Loading /> : (
        <>
          <section className="space-y-3">
            <h3 className="font-semibold">今週見るべき数字</h3>
            <div className="grid gap-4 lg:grid-cols-2">
              <FixedMetrics metrics={metrics} />
              <AutoMetrics metrics={metrics} />
            </div>
          </section>
          <section className="space-y-3">
            <h3 className="font-semibold">前回の宿題・課題</h3>
            <ul className="divide-y rounded-lg border bg-card shadow-sm">
              {actions.map((c) => <CandidateRow key={c.id} c={c} />)}
            </ul>
          </section>
          <section className="space-y-3">
            <h3 className="font-semibold">試している改善（会議で決まったこと）</h3>
            <ul className="divide-y rounded-lg border bg-card shadow-sm">
              {decisions.length > 0
                ? decisions.map((c) => <CandidateRow key={c.id} c={c} />)
                : <li className="px-4 py-3 text-sm text-muted-foreground">決定事項がありません</li>}
            </ul>
          </section>
        </>
      )}
      <hr />
      <ReportBlock metrics={metrics} />
    </div>
  )
}

// ── 案3: 1画面完結（左に数字・右にやること。資料は畳む） ──────
function Mock3({ tab }: { tab: string }) {
  const { candidates, isLoading, isPlaceholder, placeholderNote } = usePanelData(tab)
  const metrics = useCohortMetrics()
  const [openReport, setOpenReport] = useState(false)
  const todo = candidates.filter((c) => c.kind !== 'agenda').slice(0, CONFIG.carryover)

  return (
    <div className="space-y-6">
      <MockNote title="案3: 1画面で完結（資料は畳む）" body="左に数字、右にやること。スクロールせずに会議が回る。資料は必要になった人だけ開く。投影向け。" />
      {isLoading ? <Loading /> : (
        <>
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <section className="space-y-4">
              <h3 className="flex items-center gap-2 font-semibold">
                <AlertTriangle className="h-4 w-4 text-muted-foreground" aria-hidden />気にすべき数字
              </h3>
              <FixedMetrics metrics={metrics} />
              <AutoMetrics metrics={metrics} />
            </section>
            <section className="space-y-3">
              <h3 className="flex items-center gap-2 font-semibold">
                <MessageSquareText className="h-4 w-4 text-muted-foreground" aria-hidden />会議で出たこと
                {isPlaceholder && <Badge tone="warning" className="rounded-md">サンプル</Badge>}
              </h3>
              {isPlaceholder && placeholderNote && (
                <p className="rounded-md border border-yellow-600/30 bg-yellow-600/5 px-3 py-2 text-xs text-yellow-700">
                  {placeholderNote}
                </p>
              )}
              <ul className="divide-y rounded-lg border bg-card shadow-sm">
                {todo.map((c) => <CandidateRow key={c.id} c={c} />)}
              </ul>
            </section>
          </div>
          <p className="text-xs text-muted-foreground">
            数字 = {metrics?.product ?? ''} のみ・資料と同じ元データ（{metrics?.asOf} 時点）から計算。
            {isPlaceholder
              ? '宿題・決定・課題 = 会議がまだ取り込まれていないため、入る予定の見え方をサンプルで出しています。'
              : `宿題・決定・課題 = 直近のこの会議から自動抽出した ${candidates.length} 件（未承認）。`}
          </p>
        </>
      )}
      <div className="rounded-lg border bg-card shadow-sm">
        <button
          type="button"
          onClick={() => setOpenReport((v) => !v)}
          aria-expanded={openReport}
          className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        >
          {openReport ? <ChevronDown className="h-4 w-4" aria-hidden /> : <ChevronRight className="h-4 w-4" aria-hidden />}
          会議資料（詳細）を開く
          <span className="ml-auto text-xs font-normal text-muted-foreground">コンサル状況・キャンセル・着地予測・自社OP ほか</span>
        </button>
        {openReport && <div className="border-t p-4"><ReportBlock metrics={metrics} /></div>}
      </div>
    </div>
  )
}

function Loading() {
  return <p className="text-sm text-muted-foreground">読み込み中...</p>
}

function MockNote({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2">
      <p className="flex flex-wrap items-center gap-2 text-sm font-semibold">
        <FlaskConical className="h-4 w-4 text-primary" aria-hidden />{title}
        <Badge tone="muted" className="rounded-md">この案の考え方</Badge>
      </p>
      <p className="mt-1 text-sm text-muted-foreground">{body}</p>
    </div>
  )
}

export function BoardCohortPanels({ mock, tab }: { mock: CohortPanelVariant; tab: string }) {
  if (mock === '2') return <Mock2 tab={tab} />
  if (mock === '3') return <Mock3 tab={tab} />
  return <Mock1 tab={tab} />
}

/**
 * 「現在の状況」側にも同じ見方を出すための入口。
 * 会議タブ（案2/案3）と同じ計算・同じ見た目を使い、数字がタブ間でずれないようにする。
 */
export function BoardMaturedCohortBoard() {
  const metrics = useCohortMetrics()
  if (!metrics?.auto.matured) return null
  return <AutoMetrics metrics={metrics} />
}

/**
 * 専用パネルを持つ会議を、週次 snapshot にもう 1 つの取組として合成する。
 *
 * 新しいセクションを足すのではなく、既存の
 *   今週の担当・期限・課題 ／ 今週のTop判断 ／ 取組の現在地
 * にそのまま並ぶようにする。中身は会議タブ側と同じデータを使うので、
 * 「現在の状況」とタブで数字がずれない。
 */
export function useSnapshotWithCohort<T extends {
  workstreams: unknown[]
  decisions: unknown[]
  actionItems: unknown[]
}>(snapshot: T | undefined, tab: string): T | undefined {
  const metrics = useCohortMetrics()
  const { candidates } = usePanelData(tab)

  return useMemo(() => {
    if (!metrics || !snapshot) return snapshot
    const f = metrics.fixed
    const cp = f.consultationProgress
    const cxNow = f.cancellations.months[0]
    const cxPrev = f.cancellations.months[1]
    const land = f.landing.find((l) => l.n >= 100) ?? f.landing[0]
    const rec = f.consultationSplit.recent
    const prev = f.consultationSplit.previous
    // 資料・集計を根拠として1件用意し、全テキストから参照する（未参照だと「根拠未確認」になる）
    const EV_ID = 'board-cohort-metrics'
    const evidenceRefs = [{
      evidenceId: EV_ID,
      state: 'verified' as const,
      sourceType: 'salesforce',
      sourceObjectType: metrics.workstreamLabels?.sourceObjectType ?? '週次資料',
      observedAt: `${metrics.asOf}T00:00:00+09:00`,
      sourceUpdatedAt: `${metrics.asOf}T00:00:00+09:00`,
    }]
    const plain = (text: string) => ({ text, evidenceIds: [EV_ID], unknownReason: null })

    // 課題 = 会議で出た課題 + 数字から自動で拾ったもの
    const issues = [
      ...(cp.untouched && cp.untouched.left > 0
        ? [plain(`コンサル未着手 ${cp.untouched.left.toLocaleString()}件（${md(cp.untouchedFrom)} 以降の投入分・着手率${cp.untouched.rate ?? '—'}%）。消化できているのは ${md(cp.doneThrough)} まで`)]
        : []),
      // 2026-08-10: 成熟度をそろえた見方に差し替え。
      // 旧版は「投入から今日まで」で数えていたため、担当時期の偏りで率が動いていた。
      // さらに、誤差の範囲に収まる差は課題に混ぜない（件数が小さく判断できないため）。
      ...(metrics.auto.matured?.movers.items ?? [])
        .filter((x) => x.significant)
        .slice(0, 2)
        .map((x) => plain(
          `${x.axis} ${x.segment} の${x.direction === 'improved' ? 'キャンセルが改善' : 'キャンセルが悪化'}`
          + `（申込から${metrics.auto.matured!.days}日時点 ${x.previous.rate}% → ${x.recent.rate}%`
          + `・直近${x.recent.cx}/${x.recent.n}件）`,
        )),
      ...candidates.filter((c) => c.kind === 'issue').map((c) => plain(c.summary)),
    ]

    const nocDelta = rec?.nocRate != null && prev?.nocRate != null ? rec.nocRate - prev.nocRate : null
    const currentState = plain(
      `${cxNow ? `月内CX率 ${cxNow.rate}%（${cxNow.month.slice(5)}月投入 ${cxNow.cx}/${cxNow.n.toLocaleString()}${cxNow.partial ? '・進行中' : ''}${cxPrev ? `／前月 ${cxPrev.rate}%` : ''}）` : ''}`
      + `${land ? ` ／ 着地開通率 ${land.weak}〜${land.strong}%（${land.month.slice(5)}月投入）` : ''}`
      + `${nocDelta !== null ? ` ／ ノーコン率 ${rec!.nocRate}%（前2週比 ${nocDelta > 0 ? '+' : ''}${nocDelta.toFixed(1)}pt）` : ''}`,
    )

    const panelWorkstream = {
      workstreamId: 'board-panel',
      title: metrics.workstreamLabels?.title ?? 'この会議',
      ownerRef: null,
      ownerDisplayName: null,
      definitionOf100Percent: plain('—'),
      currentState,
      purpose: plain(metrics.workstreamLabels?.purpose ?? '—'),
      method: plain(metrics.workstreamLabels?.method ?? '—'),
      expectedResult: land ? plain(`着地開通率 ${land.weak}〜${land.strong}%`) : plain('—'),
      schedule: plain(`コンサル消化 ${md(cp.doneThrough)} 投入分まで（未着手 ${cp.pending.toLocaleString()}件）`),
      issues,
      weeklyAgenda: candidates.filter((c) => c.kind === 'action').map((c) => plain(c.summary)),
      decisionRequests: candidates.filter((c) => c.kind === 'decision').map((c) => plain(c.summary)),
      scheduleState: (cp.untouched && cp.untouched.left > 200 ? 'attention' : 'on_track') as 'attention' | 'on_track',
      runtimeHealth: { ...plain(`データは ${metrics.asOf} 時点`), state: 'healthy' as const },
      handoffReadiness: { ...plain('—'), state: 'unknown' as const },
      outcomeGateSet: {
        approved: false, approvedBy: null, approvedAt: null, definitionHash: null,
        version: null, gates: [], percent: null,
      },
      evidenceRefs,
    }

    // Top判断 = 会議で決まったこと（decision）を追加
    const panelDecisions = candidates.filter((c) => c.kind === 'decision').map((c) => ({
      decisionId: `board-${c.id}`,
      summary: c.summary,
      state: 'proposed' as const,
      decidedAt: null,
      ownerRef: null,
      ownerDisplayName: null,
      evidenceIds: [EV_ID],
      unknownReason: null,
    }))

    // 宿題 = 会議で出た action を追加
    const panelActions = candidates.filter((c) => c.kind === 'action').map((c) => ({
      actionItemId: `board-${c.id}`,
      summary: c.summary,
      ownerRef: c.owner ?? '',
      ownerDisplayName: c.owner,
      dueAt: c.dueAt,
      status: 'open' as const,
      evidenceIds: [EV_ID],
      unknownReason: null,
    }))

    return {
      ...snapshot,
      workstreams: [...snapshot.workstreams, panelWorkstream],
      // Top判断は proposed の先頭3件しか出ないため、開通会議の決定を先頭に置く
      decisions: [...panelDecisions, ...snapshot.decisions],
      actionItems: [...snapshot.actionItems, ...panelActions],
    }
  }, [snapshot, metrics, candidates])
}
